/**
 * The low effort ways into a review ask (c133): the quick card on Home, the share button for the tracked review
 * link, several at once (a paste, or a CSV of today's jobs) and the review card (S45). docs/52 §3.3, §3.5.
 *
 * Nothing here asks how the job went: every customer gets the same ask and the same link, straight to Google
 * (docs/43 §4). Sharing hands the link to the owner's own phone. We never send it for them.
 */
import { Banner, Button, Card, Checkbox, Chip, EmptyState, Icon, List, RecordField, Row, Segmented, TextArea, TextField } from '@ff/app-ui';
import { ASK_REFUSAL, ASK_VERDICT, REVIEW_PUBLIC, S } from '@ff/app-ui/copy';
import { reviews as R } from '@ff/contracts';
import { parseAskCsv, parseAskPaste, type ParsedAskRow, type ParsedAsks } from '@ff/messaging/parse';
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { z } from 'zod';
import { errorText, getTransport, idemKey, ownWords, pathFor, type Res } from '../lib/api.ts';
import { emailProblem, rowsToSend, shareWords, toAskRow, type AskEntry } from '../lib/asks.ts';
import { useAttest, type Attest } from '../lib/attest.ts';
import { invalidate, send, useQuery, useSend, type QueryState } from '../lib/data.ts';
import { ago, timeLabel } from '../lib/format.ts';
import { location, scoped } from '../lib/router.ts';
import { can, useReady } from '../lib/session.ts';
import { useStore } from '../lib/store.ts';
import { haptic, toast, tz } from '../lib/ui.ts';
import { Info, Loaded, Screen, Section } from '../ui/common.tsx';

type Link = z.infer<typeof R.ReviewLinkView>;
type Check = z.infer<typeof R.AskCheck>;
type CardSize = R.ReviewCardSize;

const LIMITS = R.REVIEW_ASK_LIMITS;

// ------------------------------------------------------------------------------------------ shared pieces

/** The tracked review link for the location in view. */
export function useReviewLink(enabled = true): QueryState<Link> {
  const scope = useStore(location).scope;
  return useQuery('reviewLink', { query: scope ? { location: scope } : {} }, { enabled });
}

/** After a failed submit, move focus to the first field that says what's wrong (WCAG 3.3.1). */
export function useFocusInvalid(): { ref: { current: HTMLFormElement | null }; focusInvalid: () => void } {
  const ref = useRef<HTMLFormElement>(null);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    if (tries) ref.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [tries]);
  return { ref, focusInvalid: () => setTries((n) => n + 1) };
}

/** The words for a typed email that can't be asked, or null. */
export function emailError(raw: string): string | null {
  const problem = emailProblem(raw);
  if (!problem) return null;
  return problem === 'bad_email' ? S.reviews.contactError : (ASK_VERDICT[problem] ?? S.reviews.contactError);
}

/** The attestation: in full with a tick until confirmed once, then one quiet line (docs/52 S42). */
export function AttestField({ attest, error, many }: { attest: Attest; error?: string | null; many?: boolean }): JSX.Element {
  if (attest.remembered)
    return (
      <p class="t-small t-muted">
        <Icon name="check" size={16} /> {S.reviews.attestKept}
      </p>
    );
  return (
    <Checkbox checked={attest.ticked} onChange={attest.setTicked} error={error ?? null}>
      {many ? S.reviews.attestMany : S.reviews.attest}
    </Checkbox>
  );
}

/**
 * Say what a 201 from the ask route meant. A new ask is scheduled and can be undone. A repeat inside 12 months
 * and an address that opted out both come back as a row that never sends, and the toast says so plainly.
 */
export function announceAsk(out: Res<'reviewRequestCreate'>, zone: string): void {
  const r = out.request;
  if (r.status === 'duplicate') return toast(S.reviews.alreadyAsked);
  if (r.status === 'suppressed') return toast(S.reviews.askSuppressed);
  haptic();
  toast(r.scheduledFor ? S.reviews.scheduled(timeLabel(r.scheduledFor, zone)) : S.reviews.scheduledNoTime, {
    label: S.leads.undo,
    run: () =>
      void send('reviewRequestCancel', { params: { id: r.id } })
        .then(() => (invalidate('reviewRequests', 'home'), toast(S.reviews.cancelled)))
        .catch((e: unknown) => toast(errorText(e, S.app.offline))),
  });
}

async function shareLink(link: Link): Promise<void> {
  const text = link.shareText ?? link.url;
  if (!text) return;
  const outcome = await shareWords(REVIEW_PUBLIC.shareTitle(link.businessName), text);
  if (outcome === 'copied') toast(S.app.copied);
  if (outcome === 'failed') toast(S.reviews.shareFailed);
}

/** Share the tracked review link from the owner's own phone. A quiet line stands in until the link is ready. */
export function ShareLinkButton({ link, block }: { link: QueryState<Link>; block?: boolean }): JSX.Element | null {
  const d = link.data;
  if (!d) return null;
  if (!d.ready || !d.url) return <p class="t-small t-muted">{S.reviews.linkNotReady}</p>;
  return (
    <Button icon="share" block={block} onClick={() => void shareLink(d)}>
      {S.reviews.share}
    </Button>
  );
}

// ------------------------------------------------------------------------------------------ Home: the quick ask

/** One email and Send, on Home. The first name is optional: without it the email opens "Hi there". */
export function QuickAskCard(): JSX.Element {
  const s = useReady();
  const scope = useStore(location).scope;
  const zone = useStore(tz);
  const link = useReviewLink();
  const attest = useAttest(s.me.user.id);
  const [first, setFirst] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ email?: string; attest?: string }>({});
  const [key, setKey] = useState(idemKey);
  const act = useSend();
  const { ref, focusInvalid } = useFocusInvalid();

  const submit = async (e: Event) => {
    e.preventDefault();
    const typed = email.trim();
    const errs: { email?: string; attest?: string } = {};
    const bad = emailError(typed);
    if (bad) errs.email = bad;
    if (!attest.ok) errs.attest = S.reviews.attestError;
    setErrors(errs);
    if (errs.email || errs.attest) return focusInvalid();
    attest.remember();
    const out = await act.run(() =>
      send('reviewRequestCreate', {
        body: {
          ...(first.trim() ? { firstName: first.trim() } : {}),
          email: typed,
          entry: 'quick',
          ...(scope ? { locationId: scope } : {}),
          attestation: attest.body,
          idempotencyKey: key,
        },
      }),
    );
    // On an error what they typed stays put and the same key goes with the retry.
    if (!out) return;
    setFirst('');
    setEmail('');
    setKey(idemKey());
    invalidate('reviewRequests', 'home', 'reviewLink');
    announceAsk(out, zone);
  };

  return (
    <Section title={S.home.quickAsk.title} id="quick-ask">
      <Card>
        <form ref={ref} class="stack" data-gap="m" onSubmit={submit} noValidate>
          <p class="t-muted">{S.home.quickAsk.lead}</p>
          <div class="quick-ask-fields">
            <TextField label={S.reviews.firstName} autoComplete="off" autoCapitalize="words" value={first} onInput={setFirst} maxLength={60} enterKeyHint="next" />
            <TextField label={S.reviews.contact} type="email" inputMode="email" autoComplete="off" autoCapitalize="none" spellcheck={false} value={email} onInput={setEmail} error={errors.email ?? null} maxLength={254} enterKeyHint="send" />
          </div>
          <AttestField attest={attest} error={errors.attest} />
          {act.error && <Banner tone="danger" role="alert" title={ownWords(act.error, S.errors.offlineSend)} />}
          <Button type="submit" variant="primary" busy={act.busy} icon="send">
            {S.reviews.send}
          </Button>
        </form>
        <div class="card-foot stack" data-gap="s">
          <ShareLinkButton link={link} />
          <a class="section-link" href={scoped('/reviews/ask', scope)}>
            {S.home.quickAsk.more} <Icon name="arrow-right" size={18} />
          </a>
        </div>
      </Card>
    </Section>
  );
}

// ------------------------------------------------------------------------------------------ Ask: more ways

/** Under the Ask form: share the link, the card, and for owners and managers several at once. */
export function MoreWays(): JSX.Element {
  const s = useReady();
  const scope = useStore(location).scope;
  const link = useReviewLink();
  const W = S.reviews.moreWays;
  const d = link.data;
  const ready = !!d && d.ready && !!d.url;
  const several = can(s.membership, 'review_requests.bulk');
  return (
    <Section title={W.title} id="more-ways">
      <List>
        <li>
          {ready ? (
            <Row onClick={() => void shareLink(d)} chevron={false} lead={<Icon name="share" size={22} />} title={W.share} meta={W.shareMeta} />
          ) : (
            <Row lead={<Icon name="share" size={22} />} title={W.share} meta={d ? S.reviews.linkNotReady : W.shareMeta} />
          )}
        </li>
        <li>
          <Row href={scoped('/reviews/card', scope)} lead={<Icon name="qr" size={22} />} title={W.card} meta={W.cardMeta} />
        </li>
        {several && (
          <li>
            <Row href={scoped('/reviews/ask/several', scope)} lead={<Icon name="mail" size={22} />} title={W.paste} meta={W.pasteMeta(LIMITS.pasteMax)} />
          </li>
        )}
        {several && (
          <li>
            <Row href={`${scoped('/reviews/ask/several', scope)}?mode=csv`} lead={<Icon name="file" size={22} />} title={W.csv} meta={W.csvMeta} />
          </li>
        )}
      </List>
    </Section>
  );
}

// ------------------------------------------------------------------------------------------ several at once

interface Outcome {
  created: number;
  firstSendAt: string | null;
  skipped: { row: ParsedAskRow; reason: string }[];
}

function rowTitle(r: ParsedAskRow): string {
  return r.firstName ?? r.email;
}

function rowMeta(r: ParsedAskRow, reason: string | null): string {
  return [r.firstName ? r.email : null, reason].filter(Boolean).join(' · ');
}

/** The file is read here, on the phone. Anything bigger than this isn't one day's jobs. */
const CSV_MAX_BYTES = 2_000_000;

/**
 * One way of asking several customers: read the paste or the file in the browser, have the API judge each row
 * (nothing is stored), show every verdict, then send only the rows that passed.
 */
function SeveralFlow({ entry, attest }: { entry: AskEntry; attest: Attest }): JSX.Element {
  const scope = useStore(location).scope;
  const zone = useStore(tz);
  const T = S.reviews.several;
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedAsks | null>(null);
  const [check, setCheck] = useState<Check | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [attestError, setAttestError] = useState<string | null>(null);
  const [key, setKey] = useState(idemKey);
  const checking = useSend();
  const sending = useSend();
  const fileRef = useRef<HTMLInputElement>(null);
  const { ref, focusInvalid } = useFocusInvalid();
  const where = scope ? { locationId: scope } : {};

  const forget = () => {
    setParsed(null);
    setCheck(null);
    setOutcome(null);
    setAttestError(null);
    checking.setError(null);
    sending.setError(null);
  };

  const runCheck = async (p: ParsedAsks) => {
    setParsed(p);
    setCheck(null);
    const out = await checking.run(() => send('reviewAsksCheck', { body: { entry, rows: p.rows.map(toAskRow), ...where } }));
    if (out) setCheck(out);
  };

  const onCheckPaste = (e: Event) => {
    e.preventDefault();
    forget();
    const p = parseAskPaste(text, LIMITS.checkMax);
    if (p.rows.length === 0) {
      setInputError(T.pasteEmpty);
      return focusInvalid();
    }
    setInputError(null);
    void runCheck(p);
  };

  const onFile = async (files: FileList | null) => {
    const f = files?.[0];
    if (!f) return;
    forget();
    setFileName(null);
    if (f.size > CSV_MAX_BYTES) return setInputError(T.csvTooBig);
    let raw: string;
    try {
      raw = await f.text();
    } catch {
      return setInputError(T.csvUnreadable);
    }
    // A spreadsheet saved in its own format reads as bytes, not rows.
    if (raw.includes('\u0000')) return setInputError(T.csvUnreadable);
    const p = parseAskCsv(raw, LIMITS.checkMax);
    if (!p.hasEmailColumn) return setInputError(T.csvNoEmail);
    if (!p.hasDateColumn) return setInputError(T.csvNoDate);
    if (p.rows.length === 0) return setInputError(T.csvEmpty);
    setInputError(null);
    setFileName(f.name);
    await runCheck(p);
  };

  const toSend = parsed && check ? rowsToSend(parsed.rows, check.rows, entry) : [];

  const onSend = async (e: Event) => {
    e.preventDefault();
    if (toSend.length === 0) return;
    if (!attest.ok) {
      setAttestError(S.reviews.attestError);
      return focusInvalid();
    }
    setAttestError(null);
    attest.remember();
    const sent = toSend;
    const out = await sending.run(() => send('reviewAsksSend', { body: { entry, rows: sent.map(toAskRow), ...where, attestation: attest.body, idempotencyKey: key } }));
    if (!out) return;
    haptic();
    invalidate('reviewRequests', 'home', 'reviewLink');
    setOutcome({
      created: out.created,
      firstSendAt: out.firstSendAt,
      skipped: out.skipped.flatMap((v) => {
        const row = sent[v.row];
        return row ? [{ row, reason: v.reason ?? ASK_VERDICT[v.verdict] ?? '' }] : [];
      }),
    });
    setParsed(null);
    setCheck(null);
    setText('');
    setFileName(null);
    setKey(idemKey());
  };

  if (outcome)
    return (
      <div class="stack" data-gap="m">
        <Banner tone={outcome.created ? 'ok' : 'warn'} role="status" title={outcome.created ? T.done(outcome.created) : T.doneNone}>
          {outcome.created > 0 && outcome.firstSendAt ? <p>{T.firstSend(timeLabel(outcome.firstSendAt, zone))}</p> : null}
        </Banner>
        {outcome.skipped.length > 0 && (
          <Section title={T.skipped} id={`skipped-${entry}`}>
            <List>
              {outcome.skipped.map((x, i) => (
                <li key={i}>
                  <Row title={rowTitle(x.row)} meta={rowMeta(x.row, x.reason)} chip={<Chip tone="muted">{T.skipChip}</Chip>} />
                </li>
              ))}
            </List>
          </Section>
        )}
        <div class="btn-row">
          <Button variant="primary" href={scoped('/reviews/sent', scope)}>
            {T.seeSent}
          </Button>
          <Button onClick={() => setOutcome(null)}>{T.again}</Button>
        </div>
      </div>
    );

  const verdicts = new Map((check?.rows ?? []).map((v) => [v.row, v] as const));

  return (
    <div class="stack" data-gap="l">
      {entry === 'paste' ? (
        <form ref={parsed && check ? undefined : ref} class="stack" onSubmit={onCheckPaste} noValidate>
          <p class="t-muted">{T.pasteLead(LIMITS.pasteMax)}</p>
          <TextArea
            label={T.pasteLabel}
            hint={T.pasteHint}
            rows={6}
            maxLength={20_000}
            value={text}
            error={inputError}
            onInput={(v) => {
              setText(v);
              // What was checked no longer matches what's typed.
              if (parsed) forget();
            }}
          />
          <Button type="submit" variant={check ? 'secondary' : 'primary'} busy={checking.busy}>
            {T.check}
          </Button>
        </form>
      ) : (
        <div class="stack">
          <p>{T.csvLead}</p>
          <p class="t-small t-muted">{T.csvPrivacy}</p>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => (void onFile((e.currentTarget as HTMLInputElement).files), ((e.currentTarget as HTMLInputElement).value = ''))} data-testid="csv-input" />
          <Button variant={check ? 'secondary' : 'primary'} icon="file" busy={checking.busy} onClick={() => fileRef.current?.click()}>
            {fileName ? T.csvAgain : T.csvPick}
          </Button>
          {inputError && <Banner tone="danger" role="alert" title={inputError} />}
        </div>
      )}

      {checking.error && (
        <Banner
          tone="danger"
          role="alert"
          title={ownWords(checking.error, S.errors.offlineSend)}
          action={
            entry === 'csv' && parsed ? (
              <Button size="sm" icon="refresh" onClick={() => void runCheck(parsed)}>
                {S.app.tryAgain}
              </Button>
            ) : null
          }
        />
      )}

      {parsed && check && (
        <form ref={ref} class="stack" onSubmit={onSend} noValidate>
          <Section title={T.results} id={`found-${entry}`}>
            <div class="stack" data-gap="s" role="status">
              {fileName && <p class="t-small t-muted">{T.csvRead(fileName, parsed.rows.length)}</p>}
              <p>
                <strong>{T.willAsk(toSend.length)}</strong>
              </p>
              {parsed.unread > 0 && <p class="t-small t-muted">{T.unread(parsed.unread)}</p>}
              {parsed.dropped > 0 && <p class="t-small t-muted">{T.dropped(parsed.dropped, LIMITS.checkMax)}</p>}
              <p class="t-small t-muted">{T.leftToday(check.leftToday)}</p>
            </div>
            <List label={T.results}>
              {parsed.rows.map((r, i) => {
                const v = verdicts.get(i);
                const ok = v?.verdict === 'ok';
                const reason = ok || !v ? null : (v.reason ?? ASK_VERDICT[v.verdict] ?? null);
                return (
                  <li key={i}>
                    <Row title={rowTitle(r)} meta={rowMeta(r, reason)} chip={<Chip tone={ok ? 'ok' : 'muted'}>{ok ? T.okChip : T.skipChip}</Chip>} />
                  </li>
                );
              })}
            </List>
          </Section>
          {toSend.length > 0 ? <AttestField attest={attest} error={attestError} many /> : <p id={`none-${entry}`}>{ASK_REFUSAL.nothingToSend}</p>}
          {sending.error && <Banner tone="danger" role="alert" title={ownWords(sending.error, S.errors.offlineSend)} />}
          <Button type="submit" variant="primary" size="lg" block icon="send" busy={sending.busy} disabled={toSend.length === 0} describedBy={toSend.length === 0 ? `none-${entry}` : undefined}>
            {toSend.length > 0 ? T.send(toSend.length) : S.reviews.send}
          </Button>
        </form>
      )}
    </div>
  );
}

/** Several asks at once, for owners and managers: a paste of emails, or a CSV of today's jobs. */
export function AskSeveralScreen(): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const attest = useAttest(s.me.user.id);
  const T = S.reviews.several;
  const [mode, setMode] = useState<AskEntry>(loc.query.get('mode') === 'csv' ? 'csv' : 'paste');
  return (
    <Screen title={T.title} back={scoped('/reviews/ask', loc.scope)}>
      <div class="stack form-narrow" data-gap="l">
        <Segmented label={T.mode} value={mode} onChange={setMode} options={(['paste', 'csv'] as AskEntry[]).map((m) => ({ value: m, label: T.modes[m]! }))} />
        {/* Both stay mounted so a paste isn't lost by looking at the other way in. */}
        <div hidden={mode !== 'paste'}>
          <SeveralFlow entry="paste" attest={attest} />
        </div>
        <div hidden={mode !== 'csv'}>
          <SeveralFlow entry="csv" attest={attest} />
        </div>
      </div>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ the review card (S45)

const CARD_FILE: Record<CardSize, string> = { dash: 'review-card-van-dash.pdf', leave_behind: 'review-card-leave-behind.pdf' };

/** The PDF comes through the app's own transport (cookies in production, the demo API in a demo build). */
async function downloadCard(size: CardSize, scope: string | null): Promise<void> {
  const res = await getTransport()(pathFor('reviewCard', {}, { size, ...(scope ? { location: scope } : {}) }), { method: 'GET', headers: { accept: 'application/pdf' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`review card ${res.status}`);
  const bytes = await res.blob();
  if (bytes.size === 0) throw new Error('review card empty');
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = CARD_FILE[size];
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The card as it prints, drawn from the same data: the QR code with its quiet zone, the name and the one line. */
function CardPreview({ d }: { d: Link }): JSX.Element | null {
  if (!d.qr) return null;
  const size = d.qr.size;
  const box = size + 8;
  return (
    <div class="review-card" role="group" aria-label={S.reviews.card.preview} style={`background:${d.brand.paper};color:${d.brand.ink}`}>
      <p class="review-card-name">{d.businessName}</p>
      <svg viewBox={`-4 -4 ${box} ${box}`} role="img" aria-label={S.reviews.card.qrLabel(d.businessName)} shape-rendering="crispEdges">
        <rect x={-4} y={-4} width={box} height={box} fill="#ffffff" />
        <path d={d.qr.path} fill={d.brand.ink} />
      </svg>
      <p class="review-card-line">{d.cardLine}</p>
      <span class="review-card-rule" style={`background:${d.brand.accent}`} aria-hidden="true" />
    </div>
  );
}

function CardBody({ d, link }: { d: Link; link: QueryState<Link> }): JSX.Element {
  const scope = useStore(location).scope;
  const zone = useStore(tz);
  const C = S.reviews.card;
  const [busy, setBusy] = useState<CardSize | null>(null);
  const [failed, setFailed] = useState(false);
  const download = async (size: CardSize) => {
    setBusy(size);
    setFailed(false);
    try {
      await downloadCard(size, scope);
    } catch {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };
  const t7 = d.taps.last7Days;
  const t30 = d.taps.last30Days;
  return (
    <div class="stack form-narrow" data-gap="l">
      <CardPreview d={d} />
      <div class="stack" data-gap="m">
        {d.url && <RecordField label={C.linkLabel} value={d.url} copyLabel={S.app.copy} copiedLabel={S.app.copied} />}
        <ShareLinkButton link={link} block />
      </div>
      <Section title={C.print} id="print">
        <div class="stack" data-gap="s">
          <p class="t-small t-muted">{C.printHint}</p>
          {d.cards.map((c) => (
            <Button key={c.size} icon="download" block busy={busy === c.size} disabled={busy !== null && busy !== c.size} onClick={() => void download(c.size)}>
              {C.download(C.name[c.size]!, c.widthIn, c.heightIn)}
            </Button>
          ))}
          {failed && <Banner tone="danger" role="alert" title={C.downloadFailed} />}
        </div>
      </Section>
      <Section title={C.taps} id="taps" info={<Info title={C.tapsInfo[0]} lines={C.tapsInfo.slice(1)} />}>
        <Card>
          <div class="stack" data-gap="s">
            <p>{C.tapsLine(C.last7, t7.card, t7.link, t7.ask)}</p>
            <p>{C.tapsLine(C.last30, t30.card, t30.link, t30.ask)}</p>
            <p class="t-small t-muted">{C.asks7(d.asksLast7Days)}</p>
            {d.taps.latestAt && <p class="t-small t-muted">{C.latest(ago(d.taps.latestAt, zone))}</p>}
          </div>
        </Card>
      </Section>
    </div>
  );
}

/** S45: the card preview, the link to copy or share, the print files and the taps. */
export function ReviewCardScreen(): JSX.Element {
  const scope = useStore(location).scope;
  const link = useReviewLink();
  const ready = !!link.data && link.data.ready && !!link.data.url;
  return (
    <Screen title={S.reviews.qr} back={scoped('/reviews', scope)} lead={ready ? S.reviews.card.lead : undefined} query={link}>
      <Loaded q={link} skeleton="cards">
        {(d) => (d.ready && d.url ? <CardBody d={d} link={link} /> : <EmptyState title={S.reviews.card.notReadyTitle} body={S.reviews.card.notReady} />)}
      </Loaded>
    </Screen>
  );
}
